// supportChatImage — normaliza una imagen (file picker o paste Ctrl+V) a un adjunto listo para
// subir: { blob, filename }. Reusa la técnica de NotifyPaymentModal.resizeAndCompress (canvas →
// JPEG, max 1200px, calidad 0.7), PERO devuelve un Blob (la API de soporte espera {blob, filename})
// en vez de un dataURI.
//
// GIF: se pasa CRUDO (el resize a canvas colapsa la animación a un frame → rompe el gif). El resto
// (png/jpeg/webp) se recomprime a JPEG para achicar el peso de los screenshots de bugs.

const MAX_WIDTH = 1200;
const JPEG_QUALITY = 0.7;

// True si es un tipo que NO conviene recomprimir por canvas (gif animado).
function isRawPassthrough(file) {
    return file && file.type === 'image/gif';
}

// Nombre de archivo por defecto para pastes (que no traen filename).
function defaultFilename(file, extension) {
    if (file && file.name) return file.name;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `captura-${stamp}.${extension}`;
}

// Devuelve una Promise<{blob, filename}>. Ante cualquier error de decodificación cae al archivo
// crudo (mejor mandar el original que perder el adjunto).
export function normalizeImageToAttachment(file) {
    return new Promise((resolve) => {
        if (!file) {
            resolve(null);
            return;
        }

        if (isRawPassthrough(file)) {
            resolve({blob: file, filename: defaultFilename(file, 'gif')});
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                try {
                    let {width, height} = img;
                    if (width > MAX_WIDTH) {
                        height = Math.round(height * (MAX_WIDTH / width));
                        width = MAX_WIDTH;
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    const filename = replaceExtensionWithJpg(defaultFilename(file, 'jpg'));
                    if (canvas.toBlob) {
                        canvas.toBlob(
                            (blob) => {
                                if (blob) {
                                    resolve({blob, filename});
                                } else {
                                    resolve(dataUriToAttachment(canvas.toDataURL('image/jpeg', JPEG_QUALITY), filename));
                                }
                            },
                            'image/jpeg',
                            JPEG_QUALITY,
                        );
                    } else {
                        resolve(dataUriToAttachment(canvas.toDataURL('image/jpeg', JPEG_QUALITY), filename));
                    }
                } catch (_) {
                    resolve({blob: file, filename: defaultFilename(file, 'jpg')});
                }
            };
            img.onerror = () => resolve({blob: file, filename: defaultFilename(file, 'jpg')});
            img.src = e.target.result;
        };
        reader.onerror = () => resolve({blob: file, filename: defaultFilename(file, 'jpg')});
        reader.readAsDataURL(file);
    });
}

function replaceExtensionWithJpg(filename) {
    return filename.replace(/\.[^.]+$/, '') + '.jpg';
}

// Fallback dataURI → Blob para navegadores sin canvas.toBlob (o si toBlob devolvió null).
function dataUriToAttachment(dataUri, filename) {
    try {
        const [meta, base64] = dataUri.split(',');
        const mime = (meta.match(/:(.*?);/) || [])[1] || 'image/jpeg';
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return {blob: new Blob([bytes], {type: mime}), filename};
    } catch (_) {
        return null;
    }
}

// URL local para preview del thumbnail (el caller debe revocarla al quitar el adjunto).
export function previewUrlFor(attachment) {
    if (!attachment || !attachment.blob) return null;
    try {
        return URL.createObjectURL(attachment.blob);
    } catch (_) {
        return null;
    }
}
